import { createFileRoute, Link } from "@tanstack/react-router";
import { Bike, Package, TrendingUp, Wallet, Plus } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { AdminShell } from "@/components/admin/admin-shell";
import { ShipmentStatusPill } from "@/components/admin/status-badge";
import { RegisterRiderSheet } from "@/components/admin/register-rider-form";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useDashboardStats } from "@/lib/queries";
import { formatDateTime, formatUGX, locationLabel } from "@/lib/format";
import { useUIStore } from "@/store/ui";
import { useEffect } from "react";

export const Route = createFileRoute("/dashboard")({
  component: DashboardPage,
});

function DashboardPage() {
  const stats = useDashboardStats();
  const setRegisterOpen = useUIStore((s) => s.setRegisterRiderOpen);

  // Set page title on mount
  useEffect(() => {
    document.title = "Dashboard — 24Boda Admin";
  }, []);

  return (
    <AdminShell
      title="Dashboard"
      description="Today's operations at a glance"
      actions={
        <Button onClick={() => setRegisterOpen(true)}>
          <Plus className="size-4" />
          Register rider
        </Button>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Kpi label="Total riders" value={String(stats.totalRiders)} icon={Bike} />
        <Kpi label="Riders online" value={String(stats.activeRidersToday)} icon={TrendingUp} />
        <Kpi label="Deliveries today" value={String(stats.deliveriesToday)} icon={Package} />
        <Kpi
          label="GMV today"
          value={formatUGX(stats.gmvToday)}
          hint={`Commission ${formatUGX(stats.commissionToday)}`}
          icon={Wallet}
        />
      </div>

      <Card className="rounded-lg p-0">
        <div className="flex items-center justify-between px-5 py-4">
          <div>
            <h2 className="text-sm font-semibold text-foreground">Recent shipments</h2>
            <p className="text-xs text-muted-foreground">
              {stats.activeShipments} active right now
            </p>
          </div>
          <Button asChild variant="ghost" size="sm">
            <Link to="/shipments">View all</Link>
          </Button>
        </div>
        <div className="overflow-x-auto border-t border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Created</TableHead>
                <TableHead>Pickup</TableHead>
                <TableHead>Dropoff</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Price</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {stats.isLoading && (
                <TableRow>
                  <TableCell colSpan={5} className="py-10 text-center text-muted-foreground">
                    Loading…
                  </TableCell>
                </TableRow>
              )}
              {!stats.isLoading && stats.recentShipments.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="py-10 text-center text-muted-foreground">
                    No shipments yet.
                  </TableCell>
                </TableRow>
              )}
              {stats.recentShipments.map((s) => (
                <TableRow key={s.id}>
                  <TableCell className="whitespace-nowrap">{formatDateTime(s.createdAt)}</TableCell>
                  <TableCell className="max-w-[180px] truncate">
                    {locationLabel(s.pickup)}
                  </TableCell>
                  <TableCell className="max-w-[180px] truncate">
                    {locationLabel(s.dropoff)}
                  </TableCell>
                  <TableCell>
                    <ShipmentStatusPill status={s.status} />
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatUGX(s.price.customerTotalUgx)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <RegisterRiderSheet />
    </AdminShell>
  );
}

function Kpi({
  label,
  value,
  hint,
  icon: Icon,
}: {
  label: string;
  value: string;
  hint?: string;
  icon: LucideIcon;
}) {
  return (
    <Card className="rounded-lg p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-medium text-muted-foreground">{label}</p>
          <p className="mt-2 text-2xl font-bold tracking-tight text-foreground">{value}</p>
          {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
        </div>
        <span className="grid size-9 place-items-center rounded-lg bg-primary/10 text-primary">
          <Icon className="size-4" />
        </span>
      </div>
    </Card>
  );
}
